import React, { useState } from 'react';
import { transcriptsService } from '../../services/transcripts';
import { Transcript } from '../../types';
import { SparklesIcon, ChevronDownIcon, ChevronUpIcon } from '../../icons';
import { CONTAINER_STYLES } from '../../styles/containers';
import EditButtons from '../ui/EditButtons';

interface TranscriptSummaryProps {
  transcript: Transcript | null;
  audioFileId: string;
  onTranscriptUpdate: (updatedTranscript: Transcript) => void;
}

const TranscriptSummary: React.FC<TranscriptSummaryProps> = ({
  transcript,
  audioFileId,
  onTranscriptUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!transcript?.summary) {
    return null;
  }

  const handleEditStart = () => {
    setEditedSummary(transcript.summary || '');
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  const handleEditSave = async () => {
    setIsLoading(true);
    try {
      const transcript = await transcriptsService.updateTranscript(
        audioFileId,
        { summary: editedSummary.trim() }
      );
      onTranscriptUpdate(transcript);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update transcript summary:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  // Parse the bullet points if they exist
  const formatSummary = (summary: string) => {
    // Check if the summary contains bullet points
    const lines = summary.split('\n').filter((line) => line.trim());

    // If it looks like bullet points (starts with • or - or *)
    const isBulletList = lines.some(
      (line) =>
        line.trim().startsWith('•') ||
        line.trim().startsWith('-') ||
        line.trim().startsWith('*')
    );

    if (isBulletList) {
      return (
        <ul className="space-y-2">
          {lines
            .map((line, index) => {
              // Remove bullet point characters and trim
              const cleanedLine = line.replace(/^[•\-*]\s*/, '').trim();
              if (!cleanedLine) return null;

              return (
                <li key={index} className="flex items-start">
                  <span className="text-amber-500 mr-2 mt-1 flex-shrink-0">
                    •
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {cleanedLine}
                  </span>
                </li>
              );
            })
            .filter(Boolean)}
        </ul>
      );
    }

    // If not bullet points, just display as paragraph
    return (
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {summary}
      </p>
    );
  };

  const getSummaryTypeLabel = () => {
    switch (transcript.summaryType) {
      case 'bullets':
        return 'Key Points';
      case 'bullets_verbose':
        return 'Summary';
      case 'paragraph':
        return 'Summary';
      case 'gist':
        return 'Summary';
      case 'headline':
        return 'Headline';
      default:
        return 'Summary';
    }
  };

  if (isEditing) {
    return (
      <div className={`${CONTAINER_STYLES.editMode} mb-4`}>
        <div className="flex items-center gap-2 mb-3">
          <SparklesIcon
            size={16}
            className="text-amber-600 dark:text-amber-400"
          />
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {getSummaryTypeLabel()}
          </span>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Summary
            </label>
            <textarea
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`${CONTAINER_STYLES.input} resize-none`}
              placeholder="Edit the summary..."
              rows={6}
              autoFocus
            />
          </div>
          <EditButtons
            onSave={handleEditSave}
            onCancel={handleEditCancel}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 p-4 border border-amber-200 dark:border-amber-700 rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 group cursor-pointer hover:bg-gradient-to-br hover:from-amber-100 hover:to-yellow-100 dark:hover:from-amber-900/30 dark:hover:to-yellow-900/30 transition-colors">
      <div
        className="flex items-center justify-between mb-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <SparklesIcon
            size={16}
            className="text-amber-600 dark:text-amber-400"
          />
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {getSummaryTypeLabel()}
          </span>
          {transcript.summaryModel && (
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
              {transcript.summaryModel}
            </span>
          )}
        </div>
        <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          {isExpanded ? (
            <ChevronUpIcon size={16} />
          ) : (
            <ChevronDownIcon size={16} />
          )}
        </button>
      </div>

      {isExpanded && (
        <div
          className="mt-3"
          onClick={(e) => {
            e.stopPropagation();
            handleEditStart();
          }}
        >
          {formatSummary(transcript.summary)}
        </div>
      )}
    </div>
  );
};

export default TranscriptSummary;
